// getCaptchaHeaders.js
'use strict';
import { createSelector } from '../resource/createCachedSelector.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  GEETEST_CAPTCHA_ID,
  CLOUDFLARE_TURNSTILE_SITE_KEY,
  CLOUDFLARE_TURNSTILE_KEY_TYPE,
  GEETEST_KEY_TYPE,
} from '../RemoteConfigKeys.js';

/**
 * Get Turnstile headers for API requests
 * @kind selector
 * @param {Immutable.Map} state - root state
 * @return {Object} Headers object with X-Turnstile-Token and X-Turnstile-Key-Id, or empty object
 */
const getTurnstileHeaders = createSelector(
  state => getOperationData(state, ['turnstile'], 'token'),
  state => getRemoteConfigData(state, CLOUDFLARE_TURNSTILE_KEY_TYPE),
  (token, keyType) => {
    if (token && keyType) {
      return {
        'X-Turnstile-Token': token,
        'X-Turnstile-Key-Id': keyType,
      };
    }

    return EMPTY_OBJECT;
  }
);

/**
 * Get GeeTest headers for API requests
 * @kind selector
 * @param {Immutable.Map} state - root state
 * @return {Object} Headers object with GeeTest verification data, or empty object
 */
const getGeeTestHeaders = createSelector(
  state => getOperationData(state, ['geetest'], 'lotNumber'),
  state => getOperationData(state, ['geetest'], 'captchaOutput'),
  state => getOperationData(state, ['geetest'], 'passToken'),
  state => getOperationData(state, ['geetest'], 'genTime'),
  state => getOperationData(state, ['geetest'], 'captchaId'),
  state => getRemoteConfigData(state, GEETEST_KEY_TYPE),
  (lotNumber, captchaOutput, passToken, genTime, captchaId, keyType) => {
    if (lotNumber && captchaId) {
      return {
        'X-GeeTest-Lot-Number': lotNumber,
        'X-GeeTest-Captcha-Output': captchaOutput,
        'X-GeeTest-Pass-Token': passToken,
        'X-GeeTest-Gen-Time': genTime,
        'X-GeeTest-Captcha-Id': captchaId,
        'X-GeeTest-Key-Id': keyType,
      };
    }

    return EMPTY_OBJECT;
  }
);

/**
 * Determine CAPTCHA provider based on Remote Config
 * @kind selector
 * @param {Immutable.Map} state - root state
 * @return {string|null} 'geetest' | 'turnstile' | null
 *
 * Strategy:
 * 1. Priority: GeeTest (if both captcha id and key type configured)
 * 2. Fallback: Turnstile (if both site key and key type configured)
 * 3. No verification (if both not configured)
 */
export const getCaptchaProvider = createSelector(
  state => getRemoteConfigData(state, GEETEST_CAPTCHA_ID),
  state => getRemoteConfigData(state, GEETEST_KEY_TYPE),
  state => getRemoteConfigData(state, CLOUDFLARE_TURNSTILE_SITE_KEY),
  state => getRemoteConfigData(state, CLOUDFLARE_TURNSTILE_KEY_TYPE),
  (geetestCaptchaId, geetestKeyType, turnstileSiteKey, turnstileKeyType) => {
    if (geetestCaptchaId && geetestKeyType) {
      return 'geetest';
    }

    if (turnstileSiteKey && turnstileKeyType) {
      return 'turnstile';
    }

    return null;
  }
);

/**
 * Get CAPTCHA headers (Turnstile or GeeTest) based on provider config
 * @kind selector
 * @param {Immutable.Map} state - root state
 * @return {Object} Headers object for current CAPTCHA provider
 */
const getCaptchaHeaders = createSelector(
  state => getCaptchaProvider(state),
  state => getTurnstileHeaders(state),
  state => getGeeTestHeaders(state),
  (provider, turnstileHeaders, geetestHeaders) => {
    if (provider === 'geetest' && Object.keys(geetestHeaders).length > 0) {
      return geetestHeaders;
    }

    if (provider === 'turnstile' && Object.keys(turnstileHeaders).length > 0) {
      return turnstileHeaders;
    }

    // No verification if both not configured
    return EMPTY_OBJECT;
  }
);

export default getCaptchaHeaders;
